// Shared presentational helpers for the Heliopolis ERP portal.
import { motion } from "framer-motion";
import { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n";

export function openStoredFile(path) {
  const token = localStorage.getItem("token");
  window.open(`${BACKEND_URL}/api/files/${path}?auth=${encodeURIComponent(token)}`, "_blank", "noopener");
}

export async function downloadStoredFile(path, filename) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BACKEND_URL}/api/files/${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { toast.error(res.status === 404 ? "File not found or no access" : `Download failed (${res.status})`); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function Logo({ className = "h-11", variant = "auto" }) {
  if (variant === "dark") return <img src="/heliopolis-logo-dark.png" alt="Heliopolis Pharmaceuticals" className={className} />;
  if (variant === "mark") return <img src="/heliopolis-mark.png" alt="Heliopolis Pharmaceuticals" className={className} />;
  return (
    <>
      <img src="/heliopolis-logo.png" alt="Heliopolis Pharmaceuticals" className={`${className} dark:hidden`} />
      <img src="/heliopolis-logo-dark.png" alt="Heliopolis Pharmaceuticals" className={`${className} hidden dark:block`} />
    </>
  );
}

const TONE = {
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  violet: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  indigo: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
  teal: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30",
  orange: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
  zinc: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/25",
};
const STATUS_STYLES = {
  pending: TONE.amber, requested: TONE.amber, awb_received: TONE.blue, awb_assigned: TONE.violet || TONE.blue, confirmed: TONE.emerald, approved: TONE.blue,
  rejected: TONE.rose, overdue: TONE.rose, procuring: TONE.violet, shipped: TONE.sky, delivered: TONE.emerald,
  not_started: TONE.zinc, sourcing: TONE.amber, ordered: TONE.blue, received: TONE.emerald,
  not_shipped: TONE.zinc, preparing: TONE.amber, in_transit: TONE.sky, arrived: TONE.indigo, dispatched: TONE.teal, collected: TONE.teal,
  issued: TONE.blue, paid: TONE.emerald, partially_paid: TONE.amber, draft: TONE.zinc, sent: TONE.blue, partially_fulfilled: TONE.orange,
};

export function HoldBadge({ shipment, testid = "hold-badge" }) {
  const { t } = useT();
  if (!shipment?.dispatch_hold?.active) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide bg-red-600 text-white border-red-700">
      {t("ship.onHold", "Dispatch on hold")}
    </span>
  );
}

export function StatusBadge({ status }) {
  const { t } = useT();
  const cls = STATUS_STYLES[status] || TONE.zinc;
  const label = t(`status.${status}`, String(status || "").replace(/_/g, " "));
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize tracking-wide whitespace-nowrap ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-4 border-b border-border/60 pb-5 min-w-0">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
          {subtitle}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-none mt-1 break-words">
          {title}
        </h1>
      </div>
      {actions && <div className="page-actions flex flex-wrap gap-2 min-w-0 [&>div]:contents">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "blue", testid }) {
  const tones = {
    blue: "bg-primary/10 text-primary",
    gold: "bg-accent/20 text-amber-700 dark:text-amber-300",
    green: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300",
    red: "bg-rose-500/12 text-rose-600 dark:text-rose-300",
    zinc: "bg-zinc-500/10 text-zinc-500",
  };
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="hp-card p-4 sm:p-5 flex items-start gap-3 sm:gap-4 hover:border-primary/40 hover:shadow-md transition-all min-w-0"
    >
      {Icon && <span className={`hidden sm:flex h-11 w-11 shrink-0 rounded-xl items-center justify-center ${tones[tone]}`}><Icon size={22} weight="duotone" /></span>}
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground leading-tight">{label}</p>
        <p className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 leading-none break-words">{value}</p>
      </div>
    </motion.div>
  );
}

export function Stepper({ steps, current }) {
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <div className="space-y-0">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={s.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`h-4 w-4 rounded-full border-2 shrink-0 transition-colors ${
                  active
                    ? "border-primary bg-primary"
                    : done
                    ? "border-green-600 bg-green-600"
                    : "border-zinc-300 bg-background"
                }`}
              />
              {i < steps.length - 1 && (
                <div className={`w-px flex-1 min-h-[28px] ${i < idx ? "bg-green-600" : "bg-zinc-200"}`} />
              )}
            </div>
            <div className="pb-6">
              <p className={`text-sm font-medium ${active ? "text-foreground" : done ? "text-foreground" : "text-muted-foreground"}`}>
                {s.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border rounded-2xl bg-card">
      <p className="font-heading text-lg font-bold">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const CARRIER_OPTIONS = [
  { code: "fedex", label: "FedEx" },
  { code: "dhl", label: "DHL" },
  { code: "ups", label: "UPS" },
  { code: "air_cargo", label: "Air Cargo" },
  { code: "sea_freight", label: "Sea Freight" },
  { code: "warehouse_pickup", label: "Collect at Warehouse" },
  { code: "brazil_courier", label: "Brazil Courier" },
];
export const carrierLabel = (c) => CARRIER_OPTIONS.find((x) => x.code === c)?.label || c || "—";
const CARRIER_LOGOS = ["fedex", "dhl", "ups", "air_cargo", "sea_freight", "warehouse_pickup", "brazil_courier"];
export function CarrierBadge({ carrier, size = "sm", className = "", showLabel = true }) {
  const label = carrierLabel(carrier);
  if (!CARRIER_LOGOS.includes(carrier)) return <span className={className}>{label}</span>;
  const h = size === "lg" ? "h-7" : size === "md" ? "h-6" : "h-5";
  return (
    <span className={`inline-flex items-center gap-1.5 align-middle ${className}`} data-testid={`carrier-badge-${carrier}`} title={label}>
      <img src={`/carriers/${carrier}.svg`} alt={label} className={`${h} w-auto rounded-md shadow-sm ring-1 ring-black/5 dark:ring-white/10`} />
      {showLabel && <span>{label}</span>}
    </span>
  );
}

export const DOC_TYPES = [
  { key: "commercial_invoice", label: "Commercial Invoice" },
  { key: "packing_list", label: "Packing List" },
  { key: "patient_docs", label: "Patient Documents" },
];
export const docTypeLabel = (t) => DOC_TYPES.find((x) => x.key === t)?.label || "Other";

export const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const CUR_SYMBOL = { USD: "$", EUR: "€", AED: "AED " };
export const cur = (n, c) => `${CUR_SYMBOL[(c || "USD").toUpperCase()] || `${(c || "").toUpperCase()} `}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const aed = (n) => `\u202Aد.إ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u202C`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
